import React from 'react';
import classNames from 'classnames';
import { Box, useScreenSize } from '@stitch-fix/mode-react';
import { type CloudinaryImageFields } from '../CloudinaryImageWrapper';
import styles from './style.module.scss';
import { useStickyCtaContext } from '../StickyCta/useStickyCtaContext';
import type { ContentStackJSONRichText } from '../../utils/requests/contentstack/contentStackZodSchema';
import { ModularComponentProps } from '../ModularPageWrapper/ModularPageWrapper';
import { MediaItem } from './MediaItem';
import { useAutoAdvanceTimer } from '../GalleryHero/useAutoAdvanceTimer';
import { ContentItems } from './ContentItems';
type HeroItem = {
  media: {
    mobile: CloudinaryImageFields;
    desktop: CloudinaryImageFields;
  };
  cta: {
    title: string;
    href: string;
  };
  title: ContentStackJSONRichText;
  subtitle: ContentStackJSONRichText;
  fontColor: 'white' | 'gray-16' | 'blue-45' | 'forest-60' | 'grapefruit-60';
  contentBackground: 'none' | 'white';
  buttonColor: string;
  CtaButton?: React.ReactNode;
  ctaSubtext: ContentStackJSONRichText;
};
export type HeroProps = {
  mobileLayoutVariant: 'mediaFirst' | 'contentFirst' | 'contentMediaStacked'; // contentMediaStacked is the default style for desktop and mobile
  layout?: 'split' | 'full';
  alignContent?: 'left' | 'right' | 'bottom' | 'top' | 'center';
  items?: HeroItem[];
  mobileAlignContent?: 'left' | 'right' | 'bottom' | 'top' | 'center';
  /**
   * Enables the front door offer CTA override for $20/$30 experiment.
   * Set server-side via setHeroOfferOverride utility.
   */
  enableOfferOverride?: boolean;
};
export const Hero = ({
  items = [],
  layout = 'full',
  alignContent = 'center',
  mobileLayoutVariant = 'contentMediaStacked',
  mobileAlignContent = 'center',
  verticalSpacing,
  enableOfferOverride = false
}: HeroProps & ModularComponentProps) => {
  const screenSize = useScreenSize();
  const contentAlignment = screenSize === 'sm' || screenSize === 'md' ? mobileAlignContent : alignContent;
  const {
    setStickyContainerTarget
  } = useStickyCtaContext();
  const {
    activeSlideIndex,
    shouldAnimate
  } = useAutoAdvanceTimer({
    totalItems: items.length
  });
  return <Box mt={verticalSpacing}>
      <div data-testid="container" className={classNames(styles.container, styles[contentAlignment], {
      [styles.mediaFirst]: mobileLayoutVariant === 'mediaFirst',
      [styles.contentFirst]: mobileLayoutVariant === 'contentFirst',
      [styles.splitView]: layout === 'split',
      [styles.fullView]: layout === 'full'
    })} ref={setStickyContainerTarget}>
        <div className={classNames(styles.media)}>
          {items.map((item, index) => <MediaItem
        // eslint-disable-next-line react/no-array-index-key
        key={index} slideIndex={index} activeSlideIndex={activeSlideIndex} media={item.media} initialAnimate={shouldAnimate} />)}
        </div>
        <ContentItems items={items.map(item => ({
        ...item,
        CtaButton: item.CtaButton ?? null
      }))} activeSlideIndex={activeSlideIndex} enableOfferOverride={enableOfferOverride} />
      </div>
    </Box>;
};